import { useEffect, useState, useContext } from "react";
import ordersService from "services/orders.service";
import {AuthContext} from "context";

import CircularProgress from "@mui/material/CircularProgress";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from "@mui/material/Grid";
import TextField from '@mui/material/TextField';
import MDBox from "components/MDBox";
import MDButton from 'components/MDButton';

import SelectOrganization from "cruds/fulfillment-center/components/select-organization";
import { validateOrdersData } from "../validators";

export default function CreateOrder({
    open,
    handleClose,
    orderId,
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [newOrder, setNewOrder] = useState({});
    const [selOrg, setSelOrg] = useState(null);
    const [errors, setErrors] = useState({});

    const authContext = useContext(AuthContext);

    // Handles individual field updates to the new order's state
    const changeHandler = (e) => {
        const { name, value } = e.target;
        setNewOrder({
            ...newOrder,
            [name]: value
        });
    }

    // Handles batch updates to the new order's state
    const batchChangeHandler = (updates) => {
        setNewOrder({
            ...newOrder,
            ...updates
        })
    }

    // Temporary logger for the create new order's state
    useEffect(() => {
        (async () => {
            if (open && orderId) {
                setIsLoading(true);
                try {
                    const orderData = await ordersService.getOrderById(orderId);
                    setSelOrg(orderData.data.attributes.organization);
                    const orgId = orderData.data.attributes.organization._id;
                    setNewOrder({
                        ...orderData.data.attributes,
                        organization: orgId,
                    });
                } catch (error) {
                    console.error("Error fetching order data:", error);
                } finally {
                    setIsLoading(false);
                }
            }
        }
        )();
    }, [open]);
    
    const handleCancel = () => {
        setNewOrder({});
        setSelOrg(null);
        setErrors({});
        handleClose();
    }

    // Handles the submission of the create order form
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setErrors({});
            const [isValid, validationErrors] = validateOrdersData(newOrder);
            if (!isValid) {
                setErrors(validationErrors);
                console.log("Validation errors found: ", validationErrors);
                return;
            }
            const payload = {
                data: {
                    attributes: newOrder,
                    logs: {
                        user: await authContext.getCurrentUser(),
                        comment: "",
                    }
                }
            }
            await ordersService.updateOrder(orderId, payload);
            setErrors({});
            setNewOrder({});
            setSelOrg(null);
            handleClose();

        } catch (error) {
            console.error("Error updating order: ", error);
        }
    }

    return (
        <Dialog open={open} onClose={handleCancel}>

            {isLoading ? (
                <MDBox
                    width="40vh"
                    height="30vh"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                >
                    <CircularProgress />
                </MDBox>
            ) : (
                <>
                    {/* Dialog Title and Context */}
                    <DialogTitle> Edit Order</DialogTitle>

                    <DialogContent>

                        <DialogContentText>
                            To edit the order, please input the requested information here.
                        </DialogContentText>

                        {/* Form for Creating the Order */}
                        <form onSubmit={handleSubmit} id="subscription-form">
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <TextField
                                        name="desc"
                                        type="text"
                                        multiline
                                        rows={3}
                                        label="Order Description"
                                        fullWidth
                                        onChange={changeHandler}
                                        error={errors.desc}
                                        helperText={errors.desc}
                                        value={newOrder.desc || ""}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="qty"
                                        type="number"
                                        label="Quantity"
                                        fullWidth
                                        onChange={changeHandler}
                                        error={errors.qty}
                                        helperText={errors.qty}
                                        value={newOrder.qty || ""}
                                    />

                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <SelectOrganization
                                        changeHandler={batchChangeHandler}
                                        selectedOrganization={selOrg}
                                        error={errors.organization}
                                    />
                                </Grid>
                            </Grid>


                        </form>

                    </DialogContent>

                    {/* Dialog action buttons */}
                    <DialogActions>
                        <MDButton
                            color="dark"
                            variant="gradient"
                            size="small"
                            onClick={handleCancel}>
                            Cancel
                        </MDButton>
                        <MDButton
                            color="dark"
                            variant="gradient"
                            size="small"
                            type="submit"
                            form="subscription-form">
                            Save
                        </MDButton>
                    </DialogActions>
                </>
            )}

        </Dialog >
    );
}
